import { mapGetters } from "vuex";

export default {
  data() {
    return {
      selectedMaterials: {},
      sizesSKUs: ["VELKOST", "SIZE", "VELIKOST", "ROZMIAR"],
      result: {},
      isInitialized: false,
      localProduct: null,
      isUserInteraction: false,
      useCartConfigurationEndpoint: false,
      configurationCartId: null,
    };
  },
  computed: {
    ...mapGetters({
      getMaterialsCategories: "general/getMaterialsCategories",
    }),
    filteredMaterialsByCategories() {
      const product = this.localProduct || this.productData;
      if (!product || !product.product_material_categories) {
        return [];
      }
      const materialsByCategories =
        product.product_material_categories?.reduce((acc, el) => {
          const currentCategory = this.getMaterialsCategories?.find(
            (it) => it.id === el.id
          );

          if (currentCategory) {
            acc.push({
              ...currentCategory,
              sku_enabled: el.sku_enabled,
              materials: product.product_materials?.reduce(
                (acc, material) => {
                  if (material.material_category_id === currentCategory.id) {
                    acc.push({
                      ...material,
                    });
                  }
                  return acc;
                },
                []
              ),
            });
          }
          return acc;
        }, []);
      return materialsByCategories.filter((item) => {
        return item.materials && item.materials.length > 0;
      });
    },
    ringSizeMaterialCategory() {
      const category = this.filteredMaterialsByCategories.find((el) =>
        this.sizesSKUs.includes(el?.sku)
      ) || null;
      return category;
    },
    ringSizeSelectedItem() {
      if (!this.ringSizeMaterialCategory) return null;
      return (
        this.result[this.ringSizeMaterialCategory?.slug] ||
        this.selectedMaterials[this.ringSizeMaterialCategory?.slug] ||
        null
      );
    },
    currentSku() {
      if (!Object.keys(this.result).length) {
        return null;
      }

      const newSku = Object.values(this.result).reduce((acc, el) => {
        return acc + "-" + el.sku;
      }, (this.localProduct || this.productData)?.vendor_model);

      return newSku;
    },
  },
  methods: {
    handleFilter(event) {
      if (event && event.slug && event.item) {
        this.isUserInteraction = true;
        
        const category = this.filteredMaterialsByCategories.find(
          (cat) => cat.slug === event.slug
        );
        
        const isInResult = Object.hasOwn(this.result, event.slug);
        
        if (isInResult) {
          this.onFilterClick(event, category);
        } else {
          this.onOptionsFilterClick(event, category);
        }
      }
    },
    onFilterClick(event, category) {
      if (!event || !event.item) {
        return;
      }
      
      this.result = { ...this.result, [event.slug]: event.item };
      
      if (!this.isUserInteraction) {
        return;
      }
      
      if (!category || category.sku_enabled !== 1) {
        return;
      }
      
      const isRingSize = this.sizesSKUs.includes(event.item?.material_category_sku || "");
      
      let newSku;
      const product = this.localProduct || this.productData;
      if (isRingSize) {
        const defaultSku = product?.default_sku || product?.vendor_model || "";
        const skuParts = defaultSku.split("-");
        if (skuParts.length > 1) {
          skuParts[skuParts.length - 1] = event.item.sku;
        }
        newSku = skuParts.join("-");
      } else {
        newSku = Object.values(this.result).reduce((acc, el) => {
          return acc + "-" + el.sku;
        }, product?.vendor_model);
      }
      
      this.updateConfiguration(newSku);
    },
    onOptionsFilterClick(event, category) {
      if (!event || !event.item) {
        return;
      }
      
      this.selectedMaterials = { ...this.selectedMaterials, [event.slug]: event.item };
      
      if (!this.isUserInteraction) {
        return;
      }
      
      if (!category || category.sku_enabled !== 1) {
        return;
      }
      
      const product = this.localProduct || this.productData;
      const defaultSku = product?.default_sku || product?.vendor_model || "";
      const skuParts = defaultSku.split("-");
      
      if (skuParts.length > 1) {
        skuParts[skuParts.length - 1] = event.item.sku;
      }
      
      const newSku = skuParts.join("-");
      
      this.updateConfiguration(newSku);
    },
    setMaterials() {
      const result = {};
      const additionalOptions = {};
      
      // Get materials from current configuration (variant_plp_image)
      const product = this.localProduct || this.productData;
      const configurationMaterials = 
        product?.selected_config?.materials ||
        product?.variant_plp_image?.materials ||
        product?.default_config?.materials ||
        [];
      
      this.filteredMaterialsByCategories.forEach((category) => {
        const currentMaterials = category.materials || [];
        
        // Find material that exists in configuration
        const materialFromConfig = currentMaterials.find((item) =>
          configurationMaterials.includes(item.id)
        );
        
        if (category.sku_enabled && currentMaterials.length > 0) {
          // For sku_enabled: use from configuration or first available
          result[category.slug] = materialFromConfig || currentMaterials[0];
        } else if (currentMaterials.length > 0) {
          // For sizes (not sku_enabled): use from configuration or null
          if (this.sizesSKUs.includes(category.sku)) {
            additionalOptions[category.slug] = materialFromConfig || null;
          } else {
            additionalOptions[category.slug] = currentMaterials[0];
          }
        }
      });
      
      this.result = result;
      this.selectedMaterials = additionalOptions;
      this.isInitialized = true;
    },
    async updateConfiguration(sku) {
      const skuToUse = sku || this.currentSku;
      
      if (!skuToUse) {
        return;
      }
      
      try {
        // Use cart-specific endpoint if enabled and configurationCartId is available
        const response = this.useCartConfigurationEndpoint && this.configurationCartId
          ? await this.$api.cart.getCartDiscountProductConfiguration(this.configurationCartId, skuToUse)
          : await this.$api.products.getConfiguration(skuToUse);

        if (response && response.data && response.data.data) {
          const configInfo = response.data.data;

          this.localProduct = {
            ...this.localProduct,
            default_sku: configInfo.configuration_id,
            productSKU: configInfo.configuration_id,
            ...(configInfo.cost && {
              cost: configInfo.cost,
              base_cost: configInfo.cost,
            }),
            ...(configInfo.materials && { materials: configInfo.materials }),
            ...(configInfo.translations && { translations: configInfo.translations }),
            variant_plp_image: {
              ...configInfo,
              product_configuration_id: configInfo.id,
              sku: configInfo.configuration_id,
            },
          };
          
          if (configInfo.medias && configInfo.medias.length > 0) {
            const plpImage = configInfo.medias.find((img) => img.position === "PLP");
            
            if (plpImage && plpImage.file) {
              this.localProduct = {
                ...this.localProduct,
                variant_plp_image: {
                  ...this.localProduct.variant_plp_image,
                  image: {
                    ...plpImage.file,
                  },
                },
              };
            }
          }
        }
      } catch (error) {
      }
    },
  },
};

